import { C, R, F } from '../../lib/designTokens'
import { useAsyncData } from './useProductKnowledgeData'
import * as pkd from '../../services/productKnowledgeDetailService'
import { Section, Card, Tag, Bi } from './ProductKnowledgeParts'

const KIND_BADGE = { term: ['Term / مصطلح', 'blue'], rule: ['Rule / قاعدة', 'blue'], exception_case: ['Exception / استثناء', 'blue'], output: ['Output / مخرج', 'blue'] }

function Block({ title, en, ar, mono }) {
  if (!en && !ar) return null
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>{title}</div>
      <Bi en={en} ar={ar} style={{ fontSize: F.bodyLg, color: C.ink2, lineHeight: 1.55, ...(mono ? { fontFamily: 'ui-monospace, Menlo, monospace', background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.card, padding: '10px 12px' } : {}) }} />
    </div>
  )
}

function LinkCard({ en, ar, tag, onClick }) {
  return (
    <Card onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {tag && <Tag tone="green">{tag}</Tag>}
        <span style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{en}</span>
      </div>
      {ar && <div dir="rtl" style={{ fontSize: F.body, color: C.sub }}>{ar}</div>}
    </Card>
  )
}

// One reference item: a term, a rule, a known exception or an output (opened from search,
// from the glossary, or from a screen / workflow page).
export default function ProductKnowledgeItemReader({ kind, itemKey, onOpenWorkflow, onOpenScreen }) {
  const { data: it, loading, error, reload } = useAsyncData(() => pkd.getItem(itemKey, kind), [itemKey, kind])

  if (loading) return <div style={{ color: C.sub, fontSize: F.body }}>Loading… / جارٍ التحميل…</div>
  if (error) return (
    <div>
      <p style={{ color: C.red, fontSize: F.body }}>Could not load this content. / تعذّر تحميل هذا المحتوى.</p>
      <button onClick={reload} style={{ fontSize: F.caption, padding: '6px 14px', borderRadius: R.btn, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer' }}>Retry / إعادة المحاولة</button>
    </div>
  )
  if (!it || !it.found) return <div style={{ color: C.sub, fontSize: F.body }}>Not found. / غير موجود.</div>

  const [badge, tone] = KIND_BADGE[it.kind] || [it.kind, 'blue']
  const title = it.kind === 'term' || it.kind === 'output' ? { en: it.name_en, ar: it.name_ar } : it.kind === 'exception_case' ? { en: it.meaning_en, ar: it.meaning_ar } : { en: it.statement_en, ar: it.statement_ar }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <Tag tone={tone}>{badge}</Tag>
        {it.category && <Tag>{it.category}</Tag>}
        {it.rule_type && <Tag>{it.rule_type}</Tag>}
        {it.format && <Tag>{it.format}</Tag>}
      </div>
      <h2 style={{ fontSize: F.heading, fontWeight: 800, color: C.ink, margin: '0 0 4px' }}>{title.en || it.key}</h2>
      {title.ar && <h3 style={{ fontSize: F.title, fontWeight: 600, color: C.sub, margin: '0 0 16px' }} dir="rtl">{title.ar}</h3>}

      {it.kind === 'term' && (<>
        <Block title="Definition / التعريف" en={it.short_en} ar={it.short_ar} />
        <Block title="In more detail / بمزيد من التفصيل" en={it.long_en} ar={it.long_ar} />
        <Block title="Formula / المعادلة" en={it.formula_en} ar={it.formula_ar} mono />
        {Array.isArray(it.aliases) && it.aliases.length > 0 && (
          <Block title="Also called / يُعرف أيضاً بـ" en={it.aliases.map(a => (typeof a === 'string' ? a : a.name_en || a.name || '')).filter(Boolean).join(', ')} />
        )}
      </>)}

      {it.kind === 'rule' && (<>
        <Block title="Formula / المعادلة" en={it.formula_en} ar={it.formula_ar} mono />
        {it.thresholds && (
          <Block title="Thresholds / الحدود" en={JSON.stringify(it.thresholds)} mono />
        )}
        {Array.isArray(it.applies_to) && it.applies_to.length > 0 && (
          <Section title="Applies to / تنطبق على" count={it.applies_to.length}>
            {it.applies_to.map(a => (
              <LinkCard key={a.key} en={a.name_en || a.key} ar={a.name_ar} tag={a.kind}
                onClick={a.kind === 'workflow' ? (onOpenWorkflow ? () => onOpenWorkflow(a.key) : undefined) : (onOpenScreen ? () => onOpenScreen(a.key) : undefined)} />
            ))}
          </Section>
        )}
      </>)}

      {it.kind === 'exception_case' && (<>
        {it.message && <Block title="Message shown / الرسالة الظاهرة" en={`“${it.message}”`} />}
        <Block title="Why it happens / لماذا يحدث" en={it.cause_en} ar={it.cause_ar} />
        <Block title="What to do / ما العمل" en={it.what_to_do_en} ar={it.what_to_do_ar} />
        <Block title="Who can fix it / من يستطيع الإصلاح" en={it.who_can_fix_en} ar={it.who_can_fix_ar} />
        {Array.isArray(it.occurs_in) && it.occurs_in.length > 0 && (
          <Section title="Happens in / يحدث في" count={it.occurs_in.length}>
            {it.occurs_in.map(w => <LinkCard key={w.key} en={w.name_en} ar={w.name_ar} tag="workflow" onClick={onOpenWorkflow ? () => onOpenWorkflow(w.key) : undefined} />)}
          </Section>
        )}
      </>)}

      {it.kind === 'output' && (<>
        <Block title="What it is / ما هو" en={it.description_en} ar={it.description_ar} />
        <Block title="Where you see it / أين تراه" en={it.where_visible_en} ar={it.where_visible_ar} />
        {it.producer && it.producer.workflow_key && (
          <Section title="Produced by / يُنتجه">
            <LinkCard en={it.producer.name_en || it.producer.workflow_key} ar={it.producer.name_ar} tag="workflow" onClick={onOpenWorkflow ? () => onOpenWorkflow(it.producer.workflow_key) : undefined} />
          </Section>
        )}
      </>)}
    </div>
  )
}
